<!--
  VerifyAccount.svelte — Email verification page for new accounts.

  Two modes:
  1. **From registration redirect** — URL has `?email=a@b.com` only.
     Shows a "check your email" instruction message.
  2. **From email link** — URL has `?userId=123&secretKey=ABC`.
     Auto-verifies the account on load.
-->
<script lang="ts">
	import { run } from 'svelte/legacy';

	import { Spinner } from 'flowbite-svelte';
	import { querystring } from '../../abstract-spa-router';
	import AuthLayout from './AuthLayout.svelte';
	import { AlertMessage, RoutedButton, RoutedLink } from '../../components';
	import { verifyAccount } from '../../AppNetwork';
	import { t, t2 } from '../../i18n';


	// ── State ─────────────────────────────────────────────────

	/** Whether verification completed successfully. */
	let success = $state(false);
	/** Whether a network request is in progress. */
	let loading = $state(false);
	/** Top-level error message. */
	let errorMessage = $state('');
	/** Whether auto-verify has already been attempted. */
	let attempted = $state(false);


	/**
	 * Automatically verify the account using userId + secretKey
	 * extracted from the email verification link.
	 */
	async function autoVerify(): Promise<void> {
		loading = true;
		errorMessage = '';
		/** Attempt server-side verification and surface any error. */
		try {
			await verifyAccount(Number(userIdFromUrl), secretKeyFromUrl);
			success = true;
		} catch (err: any) {
			errorMessage = err?.message || t2('Verification failed. The link may be invalid or expired.');
		} finally {
			loading = false;
		}
	}
	// ── URL params ────────────────────────────────────────────

	let params = $derived(new URLSearchParams($querystring || ''));
	let emailFromUrl = $derived(params.get('email') || '');
	let userIdFromUrl = $derived(params.get('userId') || '');
	let secretKeyFromUrl = $derived(params.get('secretKey') || '');
	// ── Auto-verify from email link ───────────────────────────

	run(() => {
		if (userIdFromUrl && secretKeyFromUrl && !attempted) {
			attempted = true;
			autoVerify();
		}
	});
</script>

{#if userIdFromUrl && secretKeyFromUrl}
	<!-- Mode 2: Auto-verifying from email link -->
	<AuthLayout heading={$t("Verifying your email")} subheading={$t("Please wait while we verify your account.")}>
		{#if loading}
			<div class="flex items-center justify-center py-8">
				<Spinner size="8" />
			</div>
		{/if}

		{#if errorMessage}
			<AlertMessage type="error" message={errorMessage} onclose={() => (errorMessage = '')} />
			<div class="mt-6 text-center">
				<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
					{$t("The verification link may have expired. Please try registering again.")}
				</p>
				<RoutedButton href="/register">{$t("Register again")}</RoutedButton>
			</div>
		{/if}

		{#if success}
			<AlertMessage
				type="success"
				message={$t("Your email has been verified! You can now sign in.")}
				dismissible={false}
			/>
			<div class="mt-6 text-center">
				<RoutedButton href="/login">{$t("Sign in")}</RoutedButton>
			</div>
		{/if}

		<!-- Back to login -->
		<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
			<a href="#/login" class="font-medium text-primary-600 hover:underline dark:text-primary-400">
				{$t("Back to sign in")}
			</a>
		</div>
	</AuthLayout>
{:else}
	<!-- Mode 1: Came from registration — show "check your email" -->
	<AuthLayout heading={$t("Check your email")} subheading={$t("We've sent you a verification link.")}>
		<div class="space-y-4 text-center">
			<div class="mb-4 text-5xl">
				<i class="fa-solid fa-envelope-circle-check text-primary-500"></i>
			</div>

			{#if emailFromUrl}
				<p class="text-gray-600 dark:text-gray-400">
					{$t("We sent a verification link to")}
					<strong class="text-gray-800 dark:text-gray-200">{emailFromUrl}</strong>.
				</p>
			{:else}
				<p class="text-gray-600 dark:text-gray-400">{$t("We sent a verification link to your email address.")}</p>
			{/if}

			<p class="text-sm text-gray-500 dark:text-gray-400">
				{$t("Click the link in the email to verify your account. If you don't see it, check your spam folder.")}
			</p>

			<div class="space-y-3 pt-4">
				<RoutedButton href="/login">{$t("Go to sign in")}</RoutedButton>
				<p class="text-sm text-gray-500 dark:text-gray-400">
					{$t("Didn't receive the email?")}
					<RoutedLink to="/register">
						<span class="text-primary-600 hover:underline dark:text-primary-400">{$t("Register again")}</span>
					</RoutedLink>
				</p>
			</div>
		</div>

		<!-- Back to login -->
		<div class="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
			<RoutedLink to="/login">
				<span class="font-medium text-primary-600 hover:underline dark:text-primary-400">{$t("Back to sign in")}</span>
			</RoutedLink>
		</div>
	</AuthLayout>
{/if}
